import { z } from 'zod';
/**
 * UI preference schemas — model-view-ux-polish Phase 3 Session 3.1 (2026-04-27).
 *
 * Per-conversation, per-user UI preferences that live in the
 * `conversations.ui_prefs` JSONB column. Distinct namespace from
 * `agent_state` so the schema itself signals "this is not engine state":
 * changes do NOT echo over Realtime, do NOT increment `revision`, and do
 * NOT invoke `recordEdit`. The PATCH endpoint shallow-merges via the
 * `merge_ui_prefs(uuid, uuid, jsonb)` SECURITY INVOKER RPC.
 *
 * The bounds `[60, 400]` are duplicated by intent in three places (UI
 * clamp during drag, schema check at the API boundary, and the SQL no-op
 * for out-of-range values via the schema's `.refine`). See
 * `apps/web/CLAUDE.md` "Drag-to-resize columns" for the heuristic.
 */
export const COL_WIDTH_MIN_PX = 60;
export const COL_WIDTH_MAX_PX = 400;
/**
 * Discrete zoom levels for the model grid — model-view-excel-parity Phase 2 S1.
 *
 * Discrete `z.union([z.literal(N), ...])` chosen over `z.number().int().min().max()`
 * so the array is predicate-checkable in the S3 keyboard handler
 * (`ZOOM_LEVELS.indexOf(current) + 1`) and the slider snaps to exactly these
 * 6 stops. Free-form zoom values are intentionally prevented.
 */
export const ZOOM_LEVELS = [50, 75, 100, 125, 150, 200];
export const zoomSchema = z.union([
    z.literal(50),
    z.literal(75),
    z.literal(100),
    z.literal(125),
    z.literal(150),
    z.literal(200),
]);
// 2026-05-26: initial zoom dropped 100 → 50 per user direction. New
// conversations + public viewer + the Ctrl+Shift+0 reset shortcut all start
// at 50%. Existing conversations with persisted `uiPrefs.zoom` keep their
// saved value (the per-conversation persistence via `merge_ui_prefs` RPC
// is unchanged). The value is still a valid member of ZOOM_LEVELS.
export const DEFAULT_ZOOM = 50;
/**
 * Per-sheet column-width map: column index (1-based, stringified to
 * satisfy JSON object-key types) → width in CSS pixels. The renderer
 * trusts whatever lands here; the API boundary clamps to the bounds.
 */
export const colWidthMapSchema = z.record(z.string().regex(/^\d+$/, 'Column index must be a non-negative integer'), z.number().int().min(COL_WIDTH_MIN_PX).max(COL_WIDTH_MAX_PX));
/**
 * Full ui_prefs payload. `colWidths` is keyed by sheet name → column-width
 * map. Other top-level keys are reserved for future UI prefs (e.g.
 * `lastVisitedSheet`); each new key MUST be additive + optional so older
 * clients ignoring the field continue to work.
 *
 * `.strict()` rejects unknown top-level keys — future fields land via PRs
 * that extend this schema, so a stale client writing a typo'd key gets a
 * 400 rather than silently storing garbage in `ui_prefs`.
 */
export const uiPrefsSchema = z
    .object({
    colWidths: z.record(z.string(), colWidthMapSchema).optional(),
    zoom: zoomSchema.optional(), // model-view-excel-parity Phase 2 S1 — additive .optional() on PARENT so .partial() inherits it
})
    .strict();
/**
 * PATCH body for `PATCH /api/conversations/:id/ui-prefs`. `.partial()`
 * makes every known top-level key optional so a PATCH carrying only `{}`
 * (or any subset) validates. Combined with `.strict()` from the parent,
 * unknown keys still 400. As new top-level keys land they MUST be
 * declared `.optional()` on `uiPrefsSchema` for `.partial()` to remain a
 * no-op delta.
 *
 * Server applies as a JSONB shallow merge via `merge_ui_prefs` RPC
 * (top-level keys overwrite — `colWidths` patch replaces the entire
 * `colWidths` object; sub-key merging is a deliberate non-feature to
 * keep semantics simple, last-write-wins per spec §7).
 */
export const uiPrefsPatchSchema = uiPrefsSchema.partial();
/**
 * Empty default — used by the API service when `conversations.ui_prefs`
 * is null or malformed (legacy rows pre-migration, or rows where no
 * PATCH has fired). Centralized so the type stays in one place.
 */
export const EMPTY_UI_PREFS = {};
